import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download, Smartphone, ArrowLeft, CheckCircle2, ChevronRight, Share, MoreVertical,
  Gift, Footprints, Bell, Activity, BarChart3, Bot, Utensils, Dumbbell, Sparkles, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useLoyaltyRules from "@/hooks/useLoyaltyRules";

// Small feature row used inside the Free/Paid tier "square". Green check
// for free tier, amber unlock for paid tier.
function FeatureRow({ icon: Icon, label, hint, tone = "free" }) {
  const dot = tone === "paid" ? "bg-amber-400/20 text-amber-300 border-amber-300/40" : "bg-emerald-500/20 text-emerald-300 border-emerald-400/40";
  return (
    <li className="flex items-start gap-2.5">
      <span className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded-lg border inline-flex items-center justify-center ${dot}`}>
        <Icon className="w-3 h-3" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white leading-tight">{label}</p>
        {hint && <p className="text-[11px] text-purple-200/80 leading-snug">{hint}</p>}
      </div>
    </li>
  );
}

/**
 * Public "Install the Companion App" landing page — no auth required.
 *
 * Lives at /companion/install. Because the path starts with `/companion`,
 * the conditional manifest script in `public/index.html` swaps in
 * `/manifest.companion.json` on first paint, so the browser's
 * `beforeinstallprompt` event references the Companion-scoped PWA. Click
 * the button → install the dedicated Companion PWA on the home screen.
 *
 * Designed to be discoverable from the landing page CTA "Install Companion
 * App on Phone" so prospects can try the PWA before subscribing.
 */
export default function CompanionInstallPage() {
  const rules = useLoyaltyRules();
  const bonusPts = Number(rules.companion_install_bonus_points || 0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

  // Try to open the installed native mobile app first. If the OS jumps into
  // the app the browser will pause / navigate away and the timeout never
  // fires. If nothing responds within 1200ms we fall back to opening the
  // web /companion route (or an app-store deep-link, whichever is provided).
  const openNativeAppOrFallback = (webFallback = "/companion") => {
    const deepLink = "glpflo://companion";
    const start = Date.now();
    let visibilityChanged = false;
    const onVis = () => { if (document.hidden) visibilityChanged = true; };
    document.addEventListener("visibilitychange", onVis);
    // Kick off the scheme handler
    try { window.location.href = deepLink; } catch (_) { /* ignore */ }
    // If the OS didn't intercept the scheme we still need to send them
    // somewhere. Give it 1.2s (long enough for iOS to prompt but short
    // enough not to leave the page frozen), then fall back.
    window.setTimeout(() => {
      document.removeEventListener("visibilitychange", onVis);
      const elapsed = Date.now() - start;
      // If the tab was hidden it means the OS did jump into the app.
      if (visibilityChanged || elapsed > 1500) return;
      // No native app installed — go to the web fallback / install flow.
      window.location.href = webFallback;
    }, 1200);
  };

  useEffect(() => {
    // Safety net for SPA navigation: programmatically ensure the manifest
    // tag is pointing at the Companion-scoped manifest before we capture
    // beforeinstallprompt. The script in index.html does this on initial
    // page load, but React Router-driven internal navigation skips it —
    // and that mismatch made installed icons open "/" instead of
    // "/companion?pwa=1".
    try {
      let link = document.querySelector('link[rel="manifest"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "manifest");
        document.head.appendChild(link);
      }
      if (link.getAttribute("href") !== "/manifest.companion.json") {
        link.setAttribute("href", "/manifest.companion.json");
      }
    } catch (_e) { /* ignore — browser will fall back to default manifest */ }

    if (isStandalone) { setInstalled(true); return undefined; }
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // Modern install detection on Chrome/Edge: check whether the
    // Companion PWA already lives on this device. Mirrors what the OS
    // tells us, so we can surface a "Open your installed app" link
    // instead of nudging install on a user who already did it.
    // Falls back silently on Safari/Firefox (API not exposed).
    if (typeof navigator !== "undefined" && typeof navigator.getInstalledRelatedApps === "function") {
      navigator
        .getInstalledRelatedApps()
        .then((apps) => {
          if (Array.isArray(apps) && apps.length > 0) setInstalled(true);
        })
        .catch(() => { /* unsupported / blocked — keep default */ });
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  const triggerInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
      } catch (_) { /* ignore */ }
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) { setShowIOSHint(true); return; }
    // Desktop / browsers without prompt support — show hint anyway.
    setShowIOSHint(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900 text-white" data-testid="companion-install-page">
      <header className="border-b border-white/10 bg-white/5 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm text-purple-100 hover:text-white inline-flex items-center gap-1" data-testid="companion-install-back">
            <ArrowLeft className="w-4 h-4" /> Back to GLPFlo
          </Link>
          <span className="text-xs uppercase tracking-widest text-purple-200">Companion Install</span>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-400 text-emerald-950 px-3 py-1 text-xs font-bold mb-4">
          <Gift className="w-3.5 h-3.5" /> Free · no subscription required to install
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Install the <span className="text-amber-300">Companion App</span>
        </h1>
        <p className="mt-3 text-base sm:text-lg text-purple-100 max-w-xl mx-auto leading-relaxed">
          Your pocket GLP-1 hub. Auto-track steps, redeem rewards, schedule shot reminders, and log measurements — all free. Subscribers unlock full nutrition + coaching.
        </p>

        {/* Primary CTA card */}
        <div className="mt-10 mx-auto max-w-md rounded-3xl bg-white/10 border border-white/20 backdrop-blur p-6 shadow-xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-300 flex items-center justify-center mb-3">
            <Smartphone className="w-7 h-7 text-purple-900" />
          </div>
          <p className="text-sm uppercase tracking-widest text-purple-200">Companion App</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Add to your home screen
          </p>

          {installed ? (
            <div className="mt-5 space-y-3" data-testid="companion-install-already">
              <div className="rounded-2xl bg-emerald-500/20 border border-emerald-300/40 px-4 py-3 text-sm text-emerald-100">
                <CheckCircle2 className="w-4 h-4 inline mr-1" /> Already installed on this device.
              </div>
              <button
                type="button"
                onClick={() => openNativeAppOrFallback("/companion")}
                className="block w-full rounded-full bg-amber-300 hover:bg-amber-400 text-purple-900 text-sm font-bold py-3 inline-flex items-center justify-center gap-2"
                data-testid="companion-install-open-existing"
              >
                Open your Companion App <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Button
                onClick={triggerInstall}
                className="mt-5 w-full bg-amber-300 hover:bg-amber-400 text-purple-900 rounded-full text-sm font-bold"
                data-testid="companion-install-cta"
              >
                <Download className="w-4 h-4 mr-2" />
                {deferredPrompt ? "Install Companion App" : isIOS ? "Save to Home Screen (iOS)" : "Show install steps"}
              </Button>
              {/* Fallback for browsers where we can't programmatically
                  detect installation (Safari, Firefox). User taps if
                  they remember installing the native app or the PWA. */}
              <button
                type="button"
                onClick={() => openNativeAppOrFallback("/companion")}
                className="block w-full mt-2 text-xs text-purple-200 hover:text-white underline-offset-4 hover:underline text-center"
                data-testid="companion-install-already-link"
              >
                Already installed? Open it →
              </button>
              {(isIOS || isAndroid) && (
                <p className="mt-2 text-[11px] text-purple-200/70 leading-snug">
                  If the native app is installed, that button opens it directly. Otherwise you&apos;ll land back here to install the mobile web version.
                </p>
              )}
            </>
          )}

          {showIOSHint && (
            <div className="mt-4 rounded-2xl bg-white/95 text-slate-800 p-4 text-left text-sm space-y-2" data-testid="companion-install-hint">
              <p className="font-bold flex items-center gap-1.5 text-purple-700">
                {isIOS ? <Share className="w-4 h-4" /> : <MoreVertical className="w-4 h-4" />}
                {isIOS ? "iOS install steps" : "Manual install steps"}
              </p>
              {isIOS ? (
                <ol className="list-decimal list-inside space-y-1 text-slate-600">
                  <li>Tap the <b>Share</b> button in Safari&apos;s toolbar.</li>
                  <li>Scroll and tap <b>&ldquo;Add to Home Screen&rdquo;</b>.</li>
                  <li>Tap <b>Add</b> — the Companion icon appears on your home screen.</li>
                </ol>
              ) : (
                <ol className="list-decimal list-inside space-y-1 text-slate-600">
                  <li>Tap the <b>menu</b> in your browser (top right).</li>
                  <li>Tap <b>&ldquo;Install app&rdquo;</b> or <b>&ldquo;Add to Home Screen&rdquo;</b>.</li>
                  <li>Tap <b>Install</b>. Done.</li>
                </ol>
              )}
            </div>
          )}

          <p className="text-[11px] text-purple-200 mt-3">
            Already installed? Open the icon directly — login lives there too.
          </p>
        </div>

        {bonusPts > 0 && (
          <div
            className="mt-6 mx-auto max-w-md rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 p-4 text-amber-950 shadow-lg text-left"
            data-testid="companion-install-bonus-banner"
          >
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
              <Gift className="w-3.5 h-3.5" /> Refer + earn
            </div>
            <h3 className="mt-1 text-base font-black leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Already a GLPFlo patient? Earn +{bonusPts.toLocaleString()} bonus points every time a friend signs up with your code.
            </h3>
            <p className="mt-1 text-xs leading-relaxed">
              Credited the moment they register — no purchase required. Grab your code from any Companion App screen and share.
            </p>
          </div>
        )}

        {/* Free vs Subscription — clean side-by-side "square" showing
            exactly what each patient gets when they install. */}
        <div className="mt-12 grid md:grid-cols-2 gap-4 text-left" data-testid="companion-tier-comparison">
          {/* FREE column */}
          <div className="rounded-3xl bg-emerald-500/10 border-2 border-emerald-300/40 p-5" data-testid="companion-tier-free">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-bold">
                <Gift className="w-4 h-4" /> Free · everyone
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-100 bg-emerald-500/30 px-2 py-0.5 rounded-full">No sub needed</span>
            </div>
            <h3 className="mt-2 text-lg font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Rewards Hub</h3>
            <ul className="mt-3 space-y-2 text-sm text-purple-50">
              <FeatureRow icon={Footprints} label="Auto step counter" hint="Ambient in-app pedometer" />
              <FeatureRow icon={Gift} label="Redeem points for coupons + refill credits" hint="1 step = 1 point" />
              <FeatureRow icon={Bell} label="Shot reminders" hint="Weekly email or SMS" />
              <FeatureRow icon={Sparkles} label="Daily rewards nudge" hint="Push at 10 AM local" />
              <FeatureRow icon={Activity} label="Body measurements + weight" hint="Tape + weight + side effects" />
              <FeatureRow icon={CheckCircle2} label="Injection logging" hint="Timestamped + site rotation" />
            </ul>
          </div>

          {/* SUBSCRIPTION column */}
          <div className="rounded-3xl bg-purple-500/10 border-2 border-amber-300/40 p-5 relative" data-testid="companion-tier-paid">
            <span className="absolute -top-3 right-4 text-[10px] font-bold uppercase tracking-wider text-purple-950 bg-amber-300 px-2.5 py-1 rounded-full shadow-md">Included with subscription</span>
            <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest font-bold">
              <Lock className="w-4 h-4" /> Subscriber unlocks
            </div>
            <h3 className="mt-2 text-lg font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Full coach</h3>
            <p className="mt-1 text-[11px] text-purple-200">Everything in Free, plus:</p>
            <ul className="mt-3 space-y-2 text-sm text-purple-50">
              <FeatureRow icon={Utensils} label="Food logging + macros" hint="Barcode + smart search" tone="paid" />
              <FeatureRow icon={Dumbbell} label="Exercise / Move logging" hint="Cardio + strength + calorie burn" tone="paid" />
              <FeatureRow icon={BarChart3} label="Calorie ring + macro rings" hint="Net calories, protein, fat, carbs" tone="paid" />
              <FeatureRow icon={Sparkles} label="Fit-Quiz weight projection" hint="Actual vs predicted curve" tone="paid" />
              <FeatureRow icon={Bot} label="AI wellness assistant" hint="Not medical advice — clinician review always separate" tone="paid" />
              <FeatureRow icon={Activity} label="Trend charts across every metric" hint="14/30/90-day insights" tone="paid" />
            </ul>
            <p className="mt-4 text-[11px] text-purple-200">
              Have a lifetime access code? Redeem it in <Link to="/profile#companion-access" className="underline hover:text-white">your profile</Link>.
            </p>
          </div>
        </div>

        {/* Secondary link — main site */}
        <Link
          to="/"
          className="mt-6 block rounded-2xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors group text-left"
          data-testid="companion-install-main-site-link"
        >
          <p className="text-xs uppercase tracking-widest text-purple-300 mb-1 inline-flex items-center gap-1">
            Looking for the main site? <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </p>
          <p className="font-bold text-base text-white">Main GLPFlo Site &amp; Login</p>
          <p className="text-purple-200 mt-1 text-xs">Marketing pages + sign-in. Install separately from the landing page footer.</p>
        </Link>

        {/* YouTube channel deep-link — patient companion users tend to
            consume how-to videos on their phone, so we drop a one-tap
            shortcut to the GLPFlo channel right here. */}
        <a
          href="https://www.youtube.com/channel/UC2NOTygbPH-3JCkElwzNEWw"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 block rounded-2xl bg-gradient-to-r from-red-500/30 to-red-600/30 border border-red-300/30 p-5 hover:from-red-500/40 hover:to-red-600/40 transition-colors group"
          data-testid="companion-youtube-link"
        >
          <p className="text-xs uppercase tracking-widest text-red-200 mb-1 inline-flex items-center gap-1">
            How-to videos &amp; patient guides <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </p>
          <p className="font-bold text-base text-white">GLPFlo on YouTube</p>
          <p className="text-purple-100 mt-1 text-xs">Injection technique, side-effect tips, storage best-practices, and Q&amp;A — straight from our clinical team.</p>
        </a>
      </section>
    </div>
  );
}
